/**
 * AdminEmployers — the SuperAdmin's employer management console.
 *
 * List every employer with their subscription snapshot, KYC status, plan,
 * team size, jobs count and outstanding invoices. Provides admin actions:
 *   · Approve/reject KYC
 *   · Impersonate (opens read-only preview)
 *   · Deactivate / Reactivate (soft — user retains read access)
 *   · Delete (hard — cascades, only after deactivation)
 *   · Edit feature preset (link to Feature Management)
 *
 * Replaces the old "Legacy admin panel" — everything a super-admin needs
 * to onboard, gate and offboard employers lives here.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import React from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useFeatures } from "@/context/FeaturesContext";
import { toast } from "sonner";
import {
    Search, Loader2, Building2, Check, X, Eye, Ban, RotateCcw, Trash2,
    Sparkles, ExternalLink, ChevronDown,
} from "lucide-react";

const STATUS_STYLE = {
    active: "border-emerald-500 text-emerald-700 bg-emerald-50",
    trialing: "border-blue-500 text-blue-700 bg-blue-50",
    grace: "border-amber-500 text-amber-700 bg-amber-50",
    past_due: "border-amber-500 text-amber-700 bg-amber-50",
    suspended: "border-rose-600 text-rose-700 bg-rose-50",
    cancelled: "border-neutral-400 text-neutral-500 bg-neutral-50",
};

const KYC_STYLE = {
    pending: "border-amber-500 text-amber-700 bg-amber-50",
    approved: "border-emerald-500 text-emerald-700 bg-emerald-50",
    rejected: "border-rose-600 text-rose-700 bg-rose-50",
};

export default function AdminEmployers() {
    const { setImpersonation } = useFeatures();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [busy, setBusy] = useState(null);   // company id currently processing
    const [expanded, setExpanded] = useState(null); // expanded row (details)
    const [page, setPage] = useState(0);
    const PAGE_SIZE = 50;
    const [total, setTotal] = useState(0);
    const [hasMore, setHasMore] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
            if (q.trim()) params.q = q.trim();
            if (statusFilter !== "all") params.status = statusFilter;
            const { data } = await api.get("/admin/employers", { params });
            setRows(data.companies || []);
            setTotal(data.total || 0);
            setHasMore(!!data.has_more);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, [q, statusFilter, page]);

    useEffect(() => { load(); }, [load]);
    // Reset to first page when filters change (guards against showing an empty
    // page when the previous filter had more results than the new one).
    useEffect(() => { setPage(0); }, [q, statusFilter]);

    async function act(cid, action, opts = {}) {
        setBusy(cid + ":" + action);
        try {
            if (action === "approve") await api.post(`/admin/companies/${cid}/approve`);
            else if (action === "reject") await api.post(`/admin/companies/${cid}/reject`);
            else if (action === "deactivate") {
                const reason = window.prompt("Reason for deactivation (visible in the audit log):", "") || "";
                if (reason === null) return;
                await api.post(`/admin/companies/${cid}/deactivate`, { reason });
            }
            else if (action === "reactivate") await api.post(`/admin/companies/${cid}/reactivate`);
            else if (action === "delete") {
                const co = rows.find((r) => r.id === cid);
                if (!window.confirm(`⚠️ HARD DELETE ${co?.name}?\n\nThis cascades to users, jobs, candidates, applications and invoices.\nThis is IRREVERSIBLE.\n\nType 'yes' to confirm.`)) return;
                await api.delete(`/admin/companies/${cid}`);
            }
            toast.success(`${action} succeeded`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(null); }
    }

    async function impersonate(co) {
        try {
            await setImpersonation({ company_id: co.id, company_name: co.name });
            toast.success(`Now viewing as ${co.name} (read-only)`);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    const filtered = useMemo(() => rows, [rows]);

    return (
        <div className="p-4 sm:p-6 max-w-[1600px] mx-auto" data-testid="admin-employers-page">
            <header className="mb-4 pb-4 border-b border-border">
                <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Super admin</div>
                <h1 className="font-display font-black text-3xl text-ikb leading-tight">Employers</h1>
                <p className="text-xs text-muted-foreground mt-1">
                    Every employer account in one place. Approve KYC, impersonate to debug, deactivate for non-payment, or delete stale demo accounts.
                </p>
            </header>

            {/* Toolbar */}
            <div className="flex items-end gap-3 mb-3 flex-wrap">
                <label className="relative">
                    <Search className="w-4 h-4 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                    <input
                        type="text"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search company name or slug…"
                        className="h-9 border border-border bg-white text-sm pl-8 pr-3 min-w-[320px]"
                        data-testid="ae-search"
                    />
                </label>
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="h-9 border border-border bg-white text-sm px-2"
                    data-testid="ae-status-filter"
                >
                    <option value="all">All statuses</option>
                    <option value="active">Active</option>
                    <option value="trialing">Trialing</option>
                    <option value="grace">Grace</option>
                    <option value="past_due">Past due</option>
                    <option value="suspended">Suspended</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="deactivated">Deactivated</option>
                </select>
                <div className="text-xs text-muted-foreground">{total} employer{total === 1 ? "" : "s"}{q || statusFilter !== "all" ? " (filtered)" : ""}</div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
            ) : (
                <>
                <div className="border border-border bg-white overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-neutral-50 border-b border-border">
                            <tr>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Company</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Owner</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Plan</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Status</th>
                                <th className="text-left px-3 py-2 text-[10px] font-mono uppercase tracking-widest">KYC</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Team</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Jobs</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest" title="Job applicants + talent-pool applicants">Applicants</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest" title="Public job views (via embed board or public apply pages)">Views</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest" title="Views → apply submits">Conv%</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Outstanding</th>
                                <th className="text-right px-3 py-2 text-[10px] font-mono uppercase tracking-widest">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((co) => {
                                const isBusy = busy?.startsWith(co.id + ":");
                                const isExp = expanded === co.id;
                                const kycStyle = KYC_STYLE[co.kyc_status] || "border-neutral-300 text-neutral-600 bg-neutral-50";
                                const subStyle = STATUS_STYLE[co.sub_status] || "border-neutral-300 text-neutral-600 bg-neutral-50";
                                const isDeactivated = !!co.deactivated_at;
                                return (
                                    <React.Fragment key={co.id}>
                                        <tr className={`border-b border-border hover:bg-secondary/40 ${isDeactivated ? "opacity-60" : ""}`} data-testid={`ae-row-${co.id}`}>
                                            <td className="px-3 py-2">
                                                <button
                                                    type="button"
                                                    onClick={() => setExpanded(isExp ? null : co.id)}
                                                    className="inline-flex items-center gap-1 text-left"
                                                >
                                                    <ChevronDown className={`w-3 h-3 transition-transform ${isExp ? "" : "-rotate-90"}`} />
                                                    <div>
                                                        <div className="font-medium">{co.name}</div>
                                                        <div className="text-[10px] font-mono text-muted-foreground">{co.company_slug}</div>
                                                    </div>
                                                </button>
                                            </td>
                                            <td className="px-3 py-2 text-xs">
                                                {co.owner ? (
                                                    <>
                                                        <div className="font-medium truncate max-w-[180px]">{co.owner.name}</div>
                                                        <div className="text-[10px] font-mono text-muted-foreground truncate max-w-[180px]">{co.owner.email}</div>
                                                    </>
                                                ) : <span className="text-muted-foreground">—</span>}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border border-ikb/30 text-ikb bg-ikb/5">
                                                    {co.sub_plan || "free"}
                                                </span>
                                                {co.preset_name && (
                                                    <div className="text-[10px] text-muted-foreground mt-0.5">Preset: {co.preset_name}</div>
                                                )}
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${subStyle}`}>
                                                    {isDeactivated ? "deactivated" : co.sub_status}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2">
                                                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 border ${kycStyle}`}>
                                                    {co.kyc_status || "unknown"}
                                                </span>
                                            </td>
                                            <td className="px-3 py-2 text-right text-xs tabular-nums">{co.team_size || 0}</td>
                                            <td className="px-3 py-2 text-right text-xs tabular-nums">{co.jobs_count || 0}</td>
                                            <td className="px-3 py-2 text-right text-xs tabular-nums" title={`Jobs: ${co.applicants_jobs || 0} · Talent pool: ${co.applicants_talent_pool || 0}`}>
                                                {co.applicants_total || 0}
                                                {co.applicants_talent_pool > 0 && (
                                                    <span className="ml-1 text-[9px] font-mono text-ikb">+TP{co.applicants_talent_pool}</span>
                                                )}
                                            </td>
                                            <td className="px-3 py-2 text-right text-xs tabular-nums">{co.public_views || 0}</td>
                                            <td className="px-3 py-2 text-right text-xs tabular-nums" title={`${co.public_apply_submits || 0} submits / ${co.public_views || 0} views`}>
                                                {co.public_views > 0 ? `${co.conversion_pct || 0}%` : <span className="text-muted-foreground">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right text-xs tabular-nums">
                                                {co.outstanding_invoices?.count ? (
                                                    <span className="text-rose-700">
                                                        {co.outstanding_invoices.count} · ₹{Math.round(co.outstanding_invoices.total || 0)}
                                                    </span>
                                                ) : <span className="text-muted-foreground">—</span>}
                                            </td>
                                            <td className="px-3 py-2 text-right">
                                                <div className="inline-flex items-center gap-1 flex-wrap justify-end">
                                                    {co.kyc_status === "pending" && (
                                                        <>
                                                            <ActionBtn testid={`ae-approve-${co.id}`} icon={Check} label="Approve" onClick={() => act(co.id, "approve")} busy={isBusy && busy.endsWith(":approve")} className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" />
                                                            <ActionBtn testid={`ae-reject-${co.id}`} icon={X} label="Reject" onClick={() => act(co.id, "reject")} busy={isBusy && busy.endsWith(":reject")} className="border-rose-500 text-rose-700 hover:bg-rose-50" />
                                                        </>
                                                    )}
                                                    <ActionBtn testid={`ae-impersonate-${co.id}`} icon={Eye} label="Impersonate" onClick={() => impersonate(co)} className="border-amber-500 text-amber-700 hover:bg-amber-50" />
                                                    <Link to={`/admin/features?cid=${co.id}`} className="inline-flex items-center gap-1 h-7 px-2 border border-ikb text-ikb bg-white hover:bg-ikb/5 text-[10px] font-mono uppercase tracking-widest" data-testid={`ae-features-${co.id}`}>
                                                        <Sparkles className="w-3 h-3" /> Features
                                                    </Link>
                                                    {isDeactivated ? (
                                                        <ActionBtn testid={`ae-reactivate-${co.id}`} icon={RotateCcw} label="Reactivate" onClick={() => act(co.id, "reactivate")} busy={isBusy && busy.endsWith(":reactivate")} className="border-emerald-500 text-emerald-700 hover:bg-emerald-50" />
                                                    ) : (
                                                        <ActionBtn testid={`ae-deactivate-${co.id}`} icon={Ban} label="Deactivate" onClick={() => act(co.id, "deactivate")} busy={isBusy && busy.endsWith(":deactivate")} className="border-neutral-500 text-neutral-700 hover:bg-neutral-100" />
                                                    )}
                                                    {isDeactivated && (
                                                        <ActionBtn testid={`ae-delete-${co.id}`} icon={Trash2} label="Delete" onClick={() => act(co.id, "delete")} busy={isBusy && busy.endsWith(":delete")} className="border-rose-500 text-rose-700 hover:bg-rose-50" />
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                        {isExp && (
                                            <tr key={co.id + "-exp"} className="border-b border-border bg-neutral-50">
                                                <td colSpan={12} className="px-4 py-3">
                                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                                                        <div>
                                                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Company ID</div>
                                                            <div className="font-mono">{co.id}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Created</div>
                                                            <div>{co.created_at?.slice(0, 10) || "—"}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Next billing</div>
                                                            <div>{co.next_billing_at?.slice(0, 10) || "—"}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Billing email</div>
                                                            <div>{co.billing_email || co.primary_email || "—"}</div>
                                                        </div>
                                                        <div>
                                                            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">GSTIN</div>
                                                            <div className="font-mono">{co.gstin || "—"}</div>
                                                        </div>
                                                        {isDeactivated && (
                                                            <div className="md:col-span-3">
                                                                <div className="text-[10px] font-mono uppercase tracking-widest text-rose-700">Deactivated</div>
                                                                <div>{co.deactivated_at?.slice(0, 10)} · Reason: {co.deactivated_reason || "—"}</div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                );
                            })}
                            {filtered.length === 0 && (
                                <tr><td colSpan={12} className="px-4 py-8 text-center text-xs text-muted-foreground">No employers match the current filter.</td></tr>
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {total > PAGE_SIZE && (
                    <div className="flex items-center justify-between mt-3 text-xs" data-testid="ae-pagination">
                        <div className="text-muted-foreground">
                            Showing {page * PAGE_SIZE + 1}–{page * PAGE_SIZE + rows.length} of <b className="tabular-nums">{total}</b>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                type="button"
                                onClick={() => setPage((p) => Math.max(0, p - 1))}
                                disabled={page === 0}
                                className="h-8 px-3 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-50 disabled:opacity-40"
                                data-testid="ae-prev"
                            >
                                ← Prev
                            </button>
                            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                                Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE_SIZE))}
                            </span>
                            <button
                                type="button"
                                onClick={() => setPage((p) => p + 1)}
                                disabled={!hasMore}
                                className="h-8 px-3 border border-border bg-white text-[10px] font-mono uppercase tracking-widest hover:bg-neutral-50 disabled:opacity-40"
                                data-testid="ae-next"
                            >
                                Next →
                            </button>
                        </div>
                    </div>
                )}
                </>
            )}
        </div>
    );
}

function ActionBtn({ icon: Icon, label, onClick, busy, className, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            disabled={busy}
            className={`inline-flex items-center gap-1 h-7 px-2 border bg-white text-[10px] font-mono uppercase tracking-widest disabled:opacity-50 ${className || "border-border"}`}
            data-testid={testid}
        >
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Icon className="w-3 h-3" />}
            {label}
        </button>
    );
}
